<template>
  <div
    v-if="totalQuantity"
    class="px-5 h-18"
  >
    <div class="relative mt-6 flex justify-between text-sm text-center whitespace-nowrap cursor-default select-none">
      <div
        class="w-6 h-6 rounded-full bg-white border border-green-400 flex-center z-10"
        @dblclick="store.reset"
      >
        0
      </div>
      <div
        v-for="size in bundleSizes"
        :key="size"
        class="w-6 h-6 rounded-full bg-white border border-green-400 flex-center transition-all z-10"
        :class="{ active: size === totalQuantity }"
      >
        {{ size }}
      </div>
      <div
        class="absolute left-1 right-3 top-2 bg-gray-100 rounded-md h-2 z-0">
        <div
          class="relative bg-green-500 rounded-md h-2 z-0 min-w-6 transition-all"
          :style="{ width: progress }"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useBundleStore } from '@/stores'
const store = useBundleStore()
const { bundleSizes } = store
const { totalQuantity } = storeToRefs(store)

const toPercent = (n: number) => `${Math.round(n * 100)}%`

const computeProgress = (
  totalQuantity: number,
  bundleSizes: number[]
) => {
  const markers = [ 0, ...bundleSizes ]
  const segments = markers.length - 1
  if (segments <= 0) return 0

  // Index of the first marker totalQuantity has not yet passed.
  let upper = markers.findIndex((m) => totalQuantity <= m)
  if (upper === -1) upper = markers.length - 1 // beyond the largest size
  if (upper === 0) return 0

  const lo = markers[upper - 1]
  const hi = markers[upper]
  let within = hi === lo ? 1 : (totalQuantity - lo) / (hi - lo)

  // When the quantity sits between two markers, hold the fill a fixed buffer
  // away from both dots. Without this, a single increment in a wide gap (e.g.
  // 16 → 32) moves the fill only a couple percent and hides behind the marker,
  // reading as "no progress". Exact marker hits (within 0 or 1) stay precise.
  const BUFFER = 0.1
  if (within > 0 && within < 1) {
    within = BUFFER + within * (1 - 2 * BUFFER)
  }
  const fraction = (upper - 1 + within) / segments

  return Math.min(fraction, 1)
}

const progress = computed(() => toPercent(computeProgress(totalQuantity.value, bundleSizes)))
</script>

<style scoped>
@reference 'tailwindcss';

.active {
  @apply border-green-500 bg-green-500 text-white scale-125 shadow-md;
}
</style>
